// App.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import '@xyflow/react/dist/style.css';
import './App.css';
import { getBackendUrl } from './config';
import AdminWorldBuilder from './admin/AdminWorldBuilder';
import RetroShell from './retro/RetroShell';

const ADMIN_PATH = '/admin/world-builder';

function App() {
  const isAdminRoute = window.location.pathname === ADMIN_PATH;

  // Track whether we are in 'login' or 'game' phase
  const [phase, setPhase] = useState("login");

  // Terminal text log
  const [messages, setMessages] = useState(["* "]);

  // Current command input
  const [command, setCommand] = useState("");

  // Command history
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyPosition, setHistoryPosition] = useState(0);

  // Whether to show password or text
  const [inputType, setInputType] = useState("text");

  // HUD data
  const [playerName, setPlayerName] = useState("");
  const [playerScore, setPlayerScore] = useState(0);
  const [playerStamina, setPlayerStamina] = useState(0);
  const [maxStamina, setMaxStamina] = useState(0);

  // Whether the input is disabled (e.g., after connection closed)
  const [inputDisabled, setInputDisabled] = useState(false);

  const [adminToken, setAdminToken] = useState(() => localStorage.getItem('adminToken') || '');

  // Socket and refs for scrolling and input selection
  const socketRef = useRef(null);
  const hasConnectedRef = useRef(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current && messagesEndRef.current.scrollIntoView) {
      // block: 'nearest' keeps ancestor containers (the retro stage) from
      // being scrolled out of position; only the output pane scrolls.
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [messages]);

  // Establish Socket.IO connection on mount
  useEffect(() => {
    const SOCKET_URL = getBackendUrl();

    socketRef.current = io(SOCKET_URL, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5000,
      randomizationFactor: 0.5,
      pingInterval: 60000,   // 60 seconds (in ms)
      pingTimeout: 180000,    // 180 seconds (in ms)
    });

    // On successful connect
    socketRef.current.on('connect', () => {
      console.log('Connected to backend.');
      setInputDisabled(false);

      if (hasConnectedRef.current) {
        // A new Socket.IO connection has a new server session. Return the UI
        // to login state so the server's fresh login prompt is handled safely.
        setPhase("login");
        setInputType("text");
        setPlayerName("");
        setCommand("");
        setHistoryPosition(0);
      }
      hasConnectedRef.current = true;
    });

    // If the server forcibly disconnects or the connection is lost
    socketRef.current.on('disconnect', (reason) => {
      if (reason === 'io client disconnect') {
        return;
      }

      const status = reason === 'io server disconnect'
        ? "Connection closed."
        : "Connection lost. Reconnecting...";
      setMessages((prev) => [...prev, status]);
      setInputDisabled(true);
    });

    socketRef.current.io?.on?.('reconnect_failed', () => {
      setMessages((prev) => [
        ...prev,
        "Unable to reconnect. Reload the page to try again.",
      ]);
      setInputDisabled(true);
    });

    // Listen for general messages from the server
    socketRef.current.on('message', (msg) => {
      setMessages((prev) => {
        let newMessages = [...prev];
        if (newMessages.length > 0 && newMessages[newMessages.length - 1] === "* ") {
          newMessages.pop();
        }
        return [...newMessages, msg, "* "];
      });
    });

    // Listen for input type changes (e.g., switching to password mode)
    socketRef.current.on('setInputType', (type) => {
      setInputType(type);
    });

    // Listen for stats updates (HUD)
    socketRef.current.on('statsUpdate', (data) => {
      setPlayerName(data.name);
      setPlayerScore(data.score);
      setPlayerStamina(data.stamina);
      setMaxStamina(data.max_stamina);
      setPhase("game");
    });

    socketRef.current.on('adminToken', (payload) => {
      const token = typeof payload === 'string' ? payload : payload?.token;
      if (!token) {
        return;
      }
      localStorage.setItem('adminToken', token);
      setAdminToken(token);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  const clearAdminToken = useCallback(() => {
    localStorage.removeItem('adminToken');
    setAdminToken('');
  }, []);

  // Handle command submission
  const handleCommandSubmit = (e) => {
    e.preventDefault();
    if (inputDisabled) {
      return;
    }

    // In game phase, if blank input is entered, just add a new prompt line and do nothing else.
    if (command.trim() === "" && phase === "game") {
      setMessages((prev) => [...prev, "* "]);
      return;
    }

    // For password input, never allow blank submissions
    if (command.trim() === "" && inputType === "password") {
      return; // Simply do nothing - don't send blank passwords
    }

    // For login phase, blank input is allowed and sent to the server.
    let outputCommand = command;
    if (inputType === "password") {
      outputCommand = "*".repeat(command.length);
    }
    setMessages((prev) => {
      let newMessages = [...prev];
      if (newMessages.length > 0 && newMessages[newMessages.length - 1] === "* ") {
        newMessages.pop();
      }
      newMessages.push(`* ${outputCommand}`);
      newMessages.push("* ");
      return newMessages;
    });
    socketRef.current.emit('command', command);

    // Only clear input during auth phase, keep it in game phase
    if (phase !== "game") {
      setCommand("");
    } else {
      // Add command to history if it has content and isn't a duplicate of the last command
      if (command.trim() !== "") {
        setCommandHistory(prevHistory => {
          if (prevHistory.length === 0 || prevHistory[0] !== command) {
            return [command, ...prevHistory]; // Add to front of array
          }
          return prevHistory;
        });
      }

      // Reset history position
      setHistoryPosition(0);

      // Select all text for easy overtyping
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.select();
        }
      }, 10);
    }
  };

  // Handle up/down arrow keys for command history
  const handleKeyDown = (e) => {
    // Only process in game phase
    if (phase !== "game") {
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();

      // If we're already at the end of history, don't go further
      if (historyPosition >= commandHistory.length) {
        return;
      }

      // If we're at position 0 (current command) and it's not in history yet,
      // and it has content, add it to history
      if (historyPosition === 0 && command.trim() !== "" &&
          (commandHistory.length === 0 || commandHistory[0] !== command)) {
        setCommandHistory(prev => [command, ...prev]);
      }

      // Move to the next position in history
      const newPosition = historyPosition + 1;
      setHistoryPosition(newPosition);

      // Set command to the history item (if available)
      if (newPosition <= commandHistory.length) {
        setCommand(commandHistory[newPosition - 1]);
      }
    }
    else if (e.key === "ArrowDown") {
      e.preventDefault();

      // If we're at position 0, can't go further down
      if (historyPosition <= 0) {
        return;
      }

      // Move to previous position in history
      const newPosition = historyPosition - 1;
      setHistoryPosition(newPosition);

      // If we're back to position 0, clear command
      if (newPosition === 0) {
        setCommand("");
      } else {
        // Set command to the history item
        setCommand(commandHistory[newPosition - 1]);
      }
    }
  };

  if (isAdminRoute) {
    return (
      <AdminRouteShell
        adminToken={adminToken}
        onAdminTokenInvalid={clearAdminToken}
        messages={messages}
        messagesEndRef={messagesEndRef}
        command={command}
        setCommand={setCommand}
        inputType={inputType}
        inputDisabled={inputDisabled}
        inputRef={inputRef}
        handleCommandSubmit={handleCommandSubmit}
        handleKeyDown={handleKeyDown}
      />
    );
  }

  return (
    <RetroShell
      onLit={() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }}
    >
      <GameTerminal
        playerName={playerName}
        playerScore={playerScore}
        playerStamina={playerStamina}
        maxStamina={maxStamina}
        messages={messages}
        messagesEndRef={messagesEndRef}
        command={command}
        setCommand={setCommand}
        inputType={inputType}
        inputDisabled={inputDisabled}
        inputRef={inputRef}
        handleCommandSubmit={handleCommandSubmit}
        handleKeyDown={handleKeyDown}
      />
    </RetroShell>
  );
}

function AdminRouteShell({
  adminToken,
  onAdminTokenInvalid,
  messages,
  messagesEndRef,
  command,
  setCommand,
  inputType,
  inputDisabled,
  inputRef,
  handleCommandSubmit,
  handleKeyDown,
}) {
  return (
    <>
      <AdminWorldBuilder adminToken={adminToken} onAdminTokenInvalid={onAdminTokenInvalid} />
      {!adminToken ? (
        <AdminLoginPanel
          messages={messages}
          messagesEndRef={messagesEndRef}
          command={command}
          setCommand={setCommand}
          inputType={inputType}
          inputDisabled={inputDisabled}
          inputRef={inputRef}
          handleCommandSubmit={handleCommandSubmit}
          handleKeyDown={handleKeyDown}
        />
      ) : null}
    </>
  );
}

function AdminLoginPanel({
  messages,
  messagesEndRef,
  command,
  setCommand,
  inputType,
  inputDisabled,
  inputRef,
  handleCommandSubmit,
  handleKeyDown,
}) {
  return (
    <aside className="admin-login-panel" aria-label="Admin login panel">
      <div className="admin-login-panel__log">
        {messages.slice(-8).map((message, index) => (
          <pre key={`${message}-${index}`}>{message}</pre>
        ))}
        <div ref={messagesEndRef} />
      </div>
      <form onSubmit={handleCommandSubmit} className="admin-login-panel__form">
        <input
          ref={inputRef}
          type={inputType}
          placeholder={inputDisabled ? 'Connection Closed' : 'Type your admin login command...'}
          value={command}
          onChange={(event) => setCommand(event.target.value)}
          onKeyDown={handleKeyDown}
          disabled={inputDisabled}
        />
      </form>
    </aside>
  );
}

function GameTerminal({
  playerName,
  playerScore,
  playerStamina,
  maxStamina,
  messages,
  messagesEndRef,
  command,
  setCommand,
  inputType,
  inputDisabled,
  inputRef,
  handleCommandSubmit,
  handleKeyDown,
}) {
  return (
    <div style={{ fontFamily: "monospace", height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Top bar / HUD */}
      <div style={{ backgroundColor: "#fe01ff", color: "#000", padding: "0.5rem" }}>
        {playerName
          ? <strong>{playerName} | Score: {playerScore}, Stamina: {playerStamina}/{maxStamina}</strong>
          : <strong>The Forgotten Realms</strong>
        }
      </div>

      {/* Main text area (blue screen) */}
      <div
        style={{
          flex: 1,
          backgroundColor: "#02ffff",
          color: "#000000",
          padding: "0.5rem",
          overflowY: "auto"
        }}
      >
        {/* Wrap text output in a container limited to 50% width */}
        <div className="output-container">
          {messages.map((msg, index) => (
            <pre key={index}>{msg}</pre>
          ))}
        </div>
        <div ref={messagesEndRef} />
      </div>

      {/* Input bar */}
      <form onSubmit={handleCommandSubmit} style={{ backgroundColor: "#ffff00", padding: "0.5rem" }}>
        <input
          ref={inputRef}
          type={inputType}
          placeholder={inputDisabled ? "Connection Closed" : "Type your command...."}
          value={command}
          onChange={(e) => setCommand(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={inputDisabled}
          style={{
            width: "100%",
            border: "none",
            outline: "none",
            backgroundColor: "#ffff00",
            fontFamily: "monospace"
          }}
        />
      </form>
    </div>
  );
}

export default App;
